import React, { useCallback, useState } from "react";
import { Platform } from "react-native";

import { Button } from "../../../../../../mads/Button";
import { useI18n } from "../../../../../../shared/I18n";
import { useTareData } from "../../../../../../shared/native-modules/tare/TareDataContext";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { RnEmployerOutreachTst } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { getEnvironmentConfig } from "../../../../config/urls";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";
import { useEmployerOutreachInfo } from "../../../../hooks/useEmployerOutreachInfo";
import { useEmployerOutreachSource } from "../../../../hooks/useEmployerOutreachSource";
import { OutreachStatus } from "../../../../hooks/useEmployerOutreachUIConfig";
import { markJobAsDisliked } from "../../../../lib/markJobAsDisliked";
import { useCloseVJ } from "../../../../lib/useCloseVJ";
import { logEmployerOutreachDecline } from "../../../../util/employerOutreachSignalsLoggingUtil";
import { DeclineSurveyPage } from "../DeclineSurvey/DeclineSurveyPage";
import { callRezContactApi } from "../EmployerOutreach/utils/rezContactApi";
import { StickyShadow } from "../StickyShadow/StickyShadow";

export interface DeclineButtonProps {
  jobKey: string;
  onDecline?: () => void;
  onError?: () => void;
}

export function useIsDeclineButtonVisible(): boolean {
  const { isFromEmployerOutreach, outreachSource } = useEmployerOutreachSource();
  const isEmployerOutreachActive =
    useViewjobProctorAllocation(RnEmployerOutreachTst).value > 1 || Platform.OS === "web";
  return isEmployerOutreachActive && isFromEmployerOutreach && !!outreachSource;
}

export function DeclineButton({
  jobKey,
  onDecline,
  onError,
}: DeclineButtonProps): React.JSX.Element | null {
  const i18n = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const {
    isFromEmployerOutreach,
    outreachSource,
    rezContactSource,
    from: entryPoint,
  } = useEmployerOutreachSource();
  const ogModel = useViewjobOneGraphModel();
  const accountKey = ogModel?.accountKey;
  const ctk = ogModel?.ctk;
  const mobtk = ogModel?.mobvjtk;
  const engagements = ogModel?.employerOutreachEngagement?.engagements;

  const isEmployerOutreachActive =
    useViewjobProctorAllocation(RnEmployerOutreachTst).value > 1 || Platform.OS === "web";

  // Check if the user has already confirmed interest
  const result = useEmployerOutreachInfo(jobKey);
  const isAlreadyConfirmed = result?.outreachInfo?.outreachStatus === OutreachStatus.INTERESTED;
  const tareData = useTareData();
  const buildEnvironment = tareData?.appModel?.buildEnvironment;
  const environmentConfig = getEnvironmentConfig(buildEnvironment);
  const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;
  const closeVJ = useCloseVJ();

  const shouldShowDeclineButton =
    isEmployerOutreachActive && isFromEmployerOutreach && !!outreachSource && !isAlreadyConfirmed;

  const handleDecline = useCallback(() => {
    if (!outreachSource) {
      return;
    }

    setIsLoading(true);

    void (async () => {
      try {
        if (outreachSource === "AUTOSOURCER") {
          await markJobAsDisliked(jobKey);

          // Log the decline interaction
          logEmployerOutreachDecline({
            employerOutreachSource: outreachSource,
            accountKey,
            ctk,
            jobKey,
            mobtk,
            isError: false,
            entryPoint,
          });

          onDecline?.();
          closeVJ();
        } else if (
          outreachSource === "SMART_SOURCING" ||
          outreachSource === "PREMIUM_MATCH_CANDIDATES"
        ) {
          if (!engagements || engagements.length === 0) {
            throw new Error("No engagement data found");
          }

          const result = await callRezContactApi({
            rezcowBaseUrl,
            endpoint: "/api/v1/contacted/decline",
            engagement: engagements[0],
            source: rezContactSource,
          });

          if (result.success) {
            // Log the decline interaction (initial decline)
            logEmployerOutreachDecline({
              employerOutreachSource: outreachSource,
              accountKey,
              ctk,
              jobKey,
              mobtk,
              isError: false,
              entryPoint,
            });
          } else {
            throw new Error(`Failed to post decline: ${result.statusCode}`);
          }
          await markJobAsDisliked(jobKey);
          setShowSurvey(true);
        }
      } catch (error) {
        console.error("[Decline Button] Failed to process decline:", error);

        // Log error if the decline flow fails
        logEmployerOutreachDecline({
          employerOutreachSource: outreachSource,
          accountKey,
          ctk,
          jobKey,
          mobtk,
          isError: true,
          entryPoint,
        });

        onError?.();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    outreachSource,
    entryPoint,
    jobKey,
    onDecline,
    accountKey,
    ctk,
    mobtk,
    engagements,
    onError,
    rezcowBaseUrl,
    rezContactSource,
    closeVJ,
  ]);

  if (!shouldShowDeclineButton) {
    return null;
  }

  return (
    /* Employer Outreach jobs will always be IA jobs, we don't show shadow for IA jobs */
    <>
      <StickyShadow enabled={false}>
        <Button
          onPress={handleDecline}
          variant="secondary"
          disabled={isLoading}
          loading={isLoading}
          size="lg"
        >
          {i18n.gettext("Decline")}
        </Button>
      </StickyShadow>
      {showSurvey ? (
        <DeclineSurveyPage
          visible={showSurvey}
          jobKey={jobKey}
          onClose={() => {
            setShowSurvey(false);
            onDecline?.();
          }}
        />
      ) : null}
    </>
  );
}
